import { useState, useEffect } from "react";
import { API } from "../App";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Calculator, Loader2, FlaskConical,
  AlertTriangle, ArrowLeft, Shield, Gauge,
  Target, Zap, Beaker, Activity, BarChart3, Clock, Wrench
} from "lucide-react";

const CALC_ICONS = {
  cpk: Gauge, ppk: Gauge, rss: Target, weibull: Activity,
  arrhenius: Zap, aet: Beaker, power: BarChart3, gage_rr: Wrench,
  aql: Shield, shelf_life: Clock, mtbf: FlaskConical,
};

const CALC_DEFAULTS = {
  cpk: { USL: 10.5, LSL: 9.5, mean: 10.0, sigma: 0.15 },
  ppk: { USL: 10.5, LSL: 9.5, mean: 10.0, sigma_overall: 0.18 },
  rss: { tolerances: [0.15, 0.12, 0.10, 0.08] },
  weibull: { beta: 2.5, eta: 5000 },
  arrhenius: { Ea: 0.7, T_use_celsius: 25, T_test_celsius: 55 },
  aet: { dose_ug_per_day: 1.0, n_devices: 1, volume_ml: 1.0, extraction_efficiency: 1.0 },
  power: { alpha: 0.05, beta: 0.10, sigma: 1.0, delta: 0.5 },
  gage_rr: { EV: 0.01, AV: 0.015, total_sigma: 0.06 },
  aql: { lot_size: 500, aql_percent: 1.0 },
  shelf_life: { real_time_months: 36, T_test_celsius: 55, T_ambient_celsius: 25, Q10: 2.0 },
  mtbf: { failure_rate_per_hour: 0.000001 },
};

const CalculatorsPage = () => {
  const { t } = useTranslation('modules');
  const [calculators, setCalculators] = useState({});
  const [selectedCalc, setSelectedCalc] = useState(null);
  const [inputs, setInputs] = useState({});
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/calculators/list`).then(res => {
      setCalculators(res.data.calculators || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const openCalc = (id) => {
    setSelectedCalc(id);
    setInputs(CALC_DEFAULTS[id] || {});
    setResult(null);
  };

  const compute = async () => {
    setComputing(true);
    try {
      const payload = { ...inputs };
      if (selectedCalc === "rss" && typeof payload.tolerances === "string") {
        payload.tolerances = payload.tolerances.split(",").map(Number).filter(n => !isNaN(n));
      }
      const res = await axios.post(`${API}/calculators/compute`, {
        calculator_id: selectedCalc, inputs: payload,
      });
      setResult(res.data);
    } catch (err) {
      setResult({ error: err.response?.data?.detail || "Computation failed" });
    }
    setComputing(false);
  };

  const updateInput = (key, val) => {
    setInputs(prev => ({
      ...prev,
      [key]: key === "tolerances" ? val : (isNaN(parseFloat(val)) ? val : parseFloat(val)),
    }));
  };

  if (loading) return <div className="min-h-screen bg-[#0A1628] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#1A6BCC] animate-spin" /></div>;

  /* ─── Calculator Detail ──────────────────────────────── */
  if (selectedCalc) {
    const calc = calculators[selectedCalc];
    const Icon = CALC_ICONS[selectedCalc] || Calculator;
    const inputKeys = Object.keys(CALC_DEFAULTS[selectedCalc] || {});

    return (
      <div className="min-h-screen bg-[#0A1628] text-white" data-testid="calculator-detail">
        <div className="sticky top-0 z-10 bg-[#0A1628]/95 backdrop-blur border-b border-[#1E3A5F]/50 px-6 py-3 flex items-center justify-between">
          <button onClick={() => { setSelectedCalc(null); setResult(null); }} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="back-to-calcs">
            <ArrowLeft className="w-4 h-4" /> All Calculators
          </button>
          <span className="text-xs text-slate-500">{calc?.standard}</span>
        </div>

        <div className="max-w-2xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#132340] border border-[#1E3A5F] flex items-center justify-center">
              <Icon className="w-5 h-5 text-[#00C8E0]" />
            </div>
            <div>
              <h1 className="text-xl font-bold" data-testid="calc-title">{calc?.name}</h1>
              <p className="text-xs text-slate-400">{calc?.description}</p>
            </div>
          </div>

          {/* Inputs */}
          <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-5 mb-4" data-testid="calc-inputs">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Inputs</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {inputKeys.map(key => (
                <div key={key}>
                  <label className="block text-[10px] text-slate-500 mb-1 uppercase">{key.replace(/_/g, " ")}</label>
                  <input
                    type={key === "tolerances" ? "text" : "number"}
                    value={key === "tolerances" ? (Array.isArray(inputs[key]) ? inputs[key].join(", ") : inputs[key]) : (inputs[key] ?? "")}
                    onChange={(e) => updateInput(key, e.target.value)}
                    step="any"
                    placeholder={key === "tolerances" ? "0.15, 0.12, 0.10" : ""}
                    className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-[#1A6BCC] focus:outline-none font-mono"
                    data-testid={`input-${key}`}
                  />
                </div>
              ))}
            </div>
            <button
              onClick={compute}
              disabled={computing}
              className="mt-4 w-full bg-[#1A6BCC] hover:bg-[#1A6BCC]/80 disabled:opacity-40 text-white py-2.5 rounded font-bold text-sm flex items-center justify-center gap-2"
              data-testid="compute-btn"
            >
              {computing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
              Calculate
            </button>
          </div>

          {/* Result */}
          {result && !result.error && (
            <div className="bg-[#0D1F38] border border-emerald-800/30 rounded-lg p-5 space-y-4" data-testid="calc-result">
              <h3 className="text-xs font-bold text-emerald-400 uppercase tracking-wider">Result</h3>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(result).filter(([k]) => !["calculator_id", "calculator_name", "standard", "computed_at", "audit_script", "error", "note", "warning", "centering_warning", "c0_note", "interpretation"].includes(k)).map(([k, v]) => (
                  <div key={k} className="bg-[#0A1628] rounded p-3">
                    <div className="text-[10px] text-slate-500 uppercase">{k.replace(/_/g, " ")}</div>
                    <div className="text-sm font-bold text-white font-mono">{typeof v === "number" ? v.toLocaleString() : String(v)}</div>
                  </div>
                ))}
              </div>

              {result.note && <div className="bg-amber-900/10 border border-amber-800/20 rounded px-3 py-2 text-xs text-amber-200">{result.note}</div>}
              {result.warning && <div className="bg-red-900/10 border border-red-800/20 rounded px-3 py-2 text-xs text-red-300"><AlertTriangle className="w-3 h-3 inline mr-1" />{result.warning}</div>}

              {result.audit_script && (
                <div className="bg-[#0A1628] border border-[#1E3A5F] rounded p-4">
                  <div className="text-[10px] text-[#00C8E0] uppercase tracking-wider mb-1 font-bold">Audit Defence Script</div>
                  <p className="text-sm text-slate-300 leading-relaxed">{result.audit_script}</p>
                </div>
              )}
            </div>
          )}
          {result?.error && <div className="bg-red-900/10 border border-red-800/20 rounded-lg p-4 text-red-300 text-sm">{result.error}</div>}
        </div>
      </div>
    );
  }

  /* ─── Calculator Grid ────────────────────────────────── */
  const categories = [...new Set(Object.values(calculators).map(c => c.category))];

  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid="calculators-page">
      <div className="border-b border-[#1E3A5F]/50 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <div className="w-8 h-8 bg-[#1A6BCC] rounded flex items-center justify-center">
            <Calculator className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-sm">Live Formula Calculators</h1>
            <p className="text-[10px] text-slate-500">11 interactive engineering calculators with audit defence scripts</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {categories.map(cat => (
          <div key={cat} className="mb-8">
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 border-b border-[#1E3A5F]/50 pb-2">{cat}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(calculators).filter(([, c]) => c.category === cat).map(([id, calc]) => {
                const Icon = CALC_ICONS[id] || Calculator;
                return (
                  <button
                    key={id}
                    onClick={() => openCalc(id)}
                    className="text-left bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-4 hover:border-[#1A6BCC]/40 transition-colors group"
                    data-testid={`calc-${id}`}
                  >
                    <Icon className="w-5 h-5 text-[#00C8E0] mb-2" />
                    <h3 className="text-sm font-bold text-white group-hover:text-[#00C8E0] transition-colors mb-1">{calc.name}</h3>
                    <p className="text-[10px] text-slate-400 leading-relaxed line-clamp-2">{calc.description}</p>
                    <div className="text-[10px] text-slate-600 mt-2">{calc.standard}</div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CalculatorsPage;
